/**
 * sync-history-helpers — czyste funkcje wspólne dla historii synchronizacji ERP.
 *
 * Wydzielone z `SyncJobsHistoryPanel`, żeby korzystał z nich też `SyncJobResultModal` bez
 * importu komponentu (i cyklu importów). Panel re-eksportuje `classifyWarning`/`buildLogEntries`
 * dla kompatybilności istniejących testów logiki.
 */

import type { BadgeColor } from '@/components/Badge';

/** Kategoria wpisu logu synchronizacji — filtr jak w Symfonii (informacje / ostrzeżenia / błędy). */
export type LogSeverity = 'info' | 'warning' | 'error';

export interface LogEntry {
  sev: LogSeverity;
  /** Nazwa rekordu (np. kontrahent), gdy dotyczy. */
  record?: string;
  text: string;
}

/** Heurystyka: ostrzeżenie czy błąd (backend trzyma je razem w `warnings`). */
export function classifyWarning(w: string): LogSeverity {
  return /pomini|b[łl][ąa]d|error|nie uda|konflikt|unique|zablokowan/i.test(w)
    ? 'error'
    : 'warning';
}

/** Buduje przeglądalne wpisy logu z `result` jobu: zmiany = informacje, warnings = ostrz./błędy. */
export function buildLogEntries(result: Record<string, unknown> | null): LogEntry[] {
  if (!result) return [];
  const entries: LogEntry[] = [];
  const changes = Array.isArray(result.changes)
    ? (result.changes as {
        record?: string;
        fields?: { field: string; from: string; to: string }[];
      }[])
    : [];
  for (const c of changes) {
    const fields = Array.isArray(c.fields) ? c.fields : [];
    const text = fields.map((f) => `${f.field}: ${f.from || '—'} → ${f.to || '—'}`).join('; ');
    entries.push({ sev: 'info', record: c.record, text });
  }
  const warnings = Array.isArray(result.warnings) ? (result.warnings as string[]) : [];
  for (const w of warnings) entries.push({ sev: classifyWarning(w), text: w });
  if (result.error) entries.push({ sev: 'error', text: String(result.error) });
  return entries;
}

/** Kategoria operacji (do chipa): eksport (push_*) / import (sync_*) / synchronizacja (*_bidirectional). */
export function syncOp(jobType: string): {
  key: 'export' | 'import' | 'sync' | 'other';
  color: BadgeColor;
} {
  if (jobType.startsWith('push_')) return { key: 'export', color: 'blue' };
  if (jobType.endsWith('_bidirectional')) return { key: 'sync', color: 'accent' };
  if (jobType.startsWith('sync_')) return { key: 'import', color: 'green' };
  return { key: 'other', color: 'gray' };
}

/**
 * Kartoteka, której dotyczy przebieg (do nagłówka wyniku): kontrahenci / pracownicy / towary /
 * słowniki. Rozpoznanie po `jobType` (ten sam wzorzec co `linkBase` w modalu wyniku).
 */
export function syncEntity(
  jobType: string,
): 'contractors' | 'employees' | 'products' | 'dictionaries' {
  if (/employee|pracownik/i.test(jobType)) return 'employees';
  if (/product|towar/i.test(jobType)) return 'products';
  if (/dictionar|slownik/i.test(jobType)) return 'dictionaries';
  return 'contractors';
}

/** Data + godzina przebiegu (kolumna „Kiedy"). Pełny znacznik, nie czas względny. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
